import { useState, useEffect, useCallback } from "react";
import useAuthToken from "./useAuthToken";
import envs from "../../config/envs";
import { requestAxios } from "../../config";

export const GET = "get";
export const HEAD = "head";
export const POST = "post";
export const PUT = "put";
export const DELETE = "delete";
export const OPTIONS = "options";
export const PATCH = "patch";
const request = requestAxios.create();

const useAjaxRequest = (params) => {
  const { initialValue } = { initialValue: null, ...params };
  const [response, setResponse] = useState({
    data: initialValue,
    error: null,
  });
  const [loading, setLoading] = useState(false);

  const [retry, setRetry] = useState(0);
  const { refreshToken, getToken } = useAuthToken();
  const performRequest = useCallback(
    async (config) => {
      await setLoading(true);
      return request
        .request(config)
        .then(({ data }) => {
          setResponse((current) => ({
            ...current,
            data,
            error: null,
          }));
          return data;
        })
        .catch((err) =>
          setResponse((current) => ({
            ...current,
            error: err,
            data: null,
          }))
        )
        .finally(async () => {
          await setLoading(false);
        });
    },
    [setLoading]
  );

  const clearRequest = useCallback(() => {
    setLoading(false);
    setResponse({
      data: initialValue,
      error: null,
    });
  }, [initialValue]);

  useEffect(() => {
    // Add a request interceptor
    const apicRequestInterceptor = request.interceptors.request.use(
      async (config) => {
        // Do something before request is sent
        await setRetry(retry + 1);
        const accessToken = await getToken();
        return {
          ...config,
          headers: {
            ...config.headers,
            Authorization: `Bearer ${accessToken}`,
            "x-ibm-client-id": envs.clientId,
            "content-type": "application/json",
            "X-Robots-Tag": envs.robots,
          },
        };
      },
      (err) => {
        // Do something with request error
        return Promise.reject(err);
      }
    );
    // Add a response interceptor
    const apicResponseInterceptor = request.interceptors.response.use(
      (res) => {
        return res;
      },
      async (err) => {
        if (
          err.response?.status === 401 &&
          retry <= Math.max(parseInt(envs.maxRetry || "1", 10), 1)
        ) {
          await refreshToken();
          return request.request(err.config);
        }
        return Promise.reject(err);
      }
    );
    return () => {
      request.interceptors.request.eject(apicRequestInterceptor);
      request.interceptors.response.eject(apicResponseInterceptor);
    };
  }, [getToken, refreshToken, retry, setLoading]);
  return { response, loading, performRequest, clearRequest };
};
export default useAjaxRequest;
